import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { CustomerAuthProvider } from 'context/CustomerAuthContext';
import {
    CustomerLoginRoute,
    LegacyTicketLookupRedirect,
    RequireCustomerAuth,
} from 'components/CustomerAuthRoutes';
import { Notification } from 'utility/notification';

// Pages
import LandingPage from 'pages/LandingPage';
import EventPage from 'pages/EventPage';
import CalendarPage from 'pages/CalendarPage';
import HostPage from 'pages/HostPage';
import SelfServe from 'pages/SelfServe';
import SelfServeEvents from 'pages/SelfServeEvents';
import SelfServeLogin from 'pages/SelfServeLogin';
import PageNotFound from 'pages/PageNotFound';
import { setPageMetadata } from 'pageMetadata';

export default function App() {
    return (
        <CustomerAuthProvider>
            <AppRoutes />
        </CustomerAuthProvider>
    );
}

function AppRoutes() {
    return (
        <>
            <PageTitle />

            <Routes>
                <Route path="/" element={<LegacyRouter />} />

                <Route path="/event">
                    <Route path=":eventId" element={<EventPage />} />
                    <Route path=":eventId/calendar" element={<CalendarPage />} />
                </Route>

                <Route path="/host" element={<HostPage />}>
                    <Route path=":hostId" element={<HostPage />} />
                </Route>

                <Route path="/tickets">
                    <Route element={<CustomerLoginRoute />}>
                        <Route index element={<SelfServeLogin />} />
                    </Route>
                    <Route element={<RequireCustomerAuth />}>
                        <Route path="event" element={<SelfServeEvents />} />
                        <Route path="event/:eventId" element={<SelfServe />} />
                    </Route>
                </Route>

                <Route path="/ticket_lookup" element={<LegacyTicketLookupRedirect />} />

                <Route path="*" element={<PageNotFound />} />
            </Routes>

            <Notification />
        </>
    );
}

/**
 * Updates the title and description tags whenever the user navigates to a new page
 *
 * Note: This is only for static pages. Host and event screens will dynamically set these tags based on the host/event being viewed
 *
 * Note: Web crawlers don't seem to be interpreting javascript so these values not not likely being used.
 *       We'll keep updating the tab in case that changes, but we may have to move to server-side rendering at some point.
 */
function PageTitle() {
    const location = useLocation();

    useEffect(() => {
        switch (location.pathname) {
            case '/':
                setPageMetadata(null); // Reset to default meta data
                break;
            case '/tickets':
            case '/tickets/':
                setPageMetadata({
                    title: 'Find your tickets',
                    description:
                        'Enter your email address to receive a link to access your tickets.',
                    image: null,
                });
                break;
            case '/tickets/event':
            case '/tickets/event/':
                setPageMetadata({
                    title: 'Your events',
                    description: 'View the events associated with your account.',
                    image: null,
                });
                break;
            default:
                // Event and Host pages set these values themselves based on the event/host being viewed
                break;
        }
    }, [location.pathname]);

    return null;
}

// Handle old routes that were phased out in favour of the hash routing above
// Before, we just rendered pages based on query params in the url. These links could still be out in the wild

function LegacyRouter() {
    const [ready, setReady] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const url = new URL(document.location);

        const hash = url.hash;
        const event = url.searchParams.get('e');
        const host = url.searchParams.get('o');

        if (hash) {
            navigate(hash.replace('#', '') + url.search, { replace: true });
        } else if (event) {
            url.searchParams.delete('e');
            url.searchParams.delete('o');
            navigate(`/event/${event}` + url.search, { replace: true });
        } else if (host) {
            url.searchParams.delete('e');
            url.searchParams.delete('o');
            navigate(`/host/${host}` + url.search, { replace: true });
        } else {
            setReady(true);
        }
        // eslint-disable-next-line
    }, []);

    if (ready) {
        return <LandingPage />;
    } else {
        return null;
    }
}
